import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  AlertTriangle, CheckCircle2, ArrowRight, Target, Rocket, X,
  Send, Clock, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import { Loading, Chip } from "./_primitives";
import SupportCenter from "./SupportCenter";
import OnboardingCommand from "./OnboardingCommand";
import ReliabilityCenter from "./ReliabilityCenter";
import CleanlinessCenter from "./CleanlinessCenter";
import AdoptionAnalytics from "./AdoptionAnalytics";
import ActivityCenter from "./ActivityCenter";

/* ───────────────────────── Drill-down drawer ─────────────────────── */

function Drawer({ open, onClose, title, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[60] flex" data-testid="drilldown-drawer">
      <div className="flex-1 bg-black/60" onClick={onClose} />
      <div className="w-full md:w-[820px] max-w-full bg-[var(--fop-bg)] border-l border-[var(--fop-border)] overflow-y-auto">
        <div className="sticky top-0 bg-[var(--fop-bg)] border-b border-[var(--fop-border)] px-5 py-3 flex items-center justify-between z-10">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">{title}</div>
          <button onClick={onClose} className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]" data-testid="drawer-close"><X size={16} /></button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

/* ───────────────────────── Triage tile ───────────────────────────── */

function Tile({ testid, accent, label, value, sub, onClick }) {
  const accentMap = {
    red: "border-red-500/40 bg-red-500/5 hover:bg-red-500/10",
    amber: "border-amber-500/40 bg-amber-500/5 hover:bg-amber-500/10",
    green: "border-emerald-500/40 bg-emerald-500/5 hover:bg-emerald-500/10",
    zinc: "border-[var(--fop-border)] hover:bg-[var(--fop-surface)]",
  };
  const valueColor = { red: "text-red-300", amber: "text-amber-300", green: "text-emerald-300", zinc: "text-[var(--fop-text)]" };
  return (
    <button onClick={onClick} data-testid={testid}
      className={`text-left fop-surface-2 rounded-lg p-4 border transition-colors ${accentMap[accent] || accentMap.zinc} group`}>
      <div className="flex items-start justify-between">
        <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-[var(--fop-text-3)]">{label}</div>
        <ArrowRight size={12} className="text-[var(--fop-text-4)] group-hover:text-[var(--fop-primary)] group-hover:translate-x-0.5 transition" />
      </div>
      <div className={`font-display text-3xl mt-1 ${valueColor[accent] || valueColor.zinc}`}>{value}</div>
      {sub && <div className="text-[11px] text-[var(--fop-text-3)] mt-0.5">{sub}</div>}
    </button>
  );
}

/* ───────────────────────── Command Center ────────────────────────── */

function errMsg(e, fallback) {
  return e?.response?.data?.detail || fallback;
}

export default function CommandCenter({ onOpenOnboarding }) {
  const [actions, setActions] = useState(null);
  const [health, setHealth] = useState(null);
  const [success, setSuccess] = useState(null);
  const [support, setSupport] = useState(null);
  const [onb, setOnb] = useState(null);
  const [adopt, setAdopt] = useState(null);
  const [rel, setRel] = useState(null);
  const [clean, setClean] = useState(null);
  const [drawer, setDrawer] = useState(null);
  const [recentResend, setRecentResend] = useState(null); // sticky resend confirmation
  // Hard-Gate P1 (2026-09-06) — every /platform/* call below now requires
  // MFA enrollment (`require_platform_admin` -> `_mfa_gate`). Previously a
  // 403 here just toasted and left every state variable `null` forever —
  // the component never left the loading spinner. Track a distinct
  // terminal error state instead so the UI always resolves.
  const [loadError, setLoadError] = useState(null);
  const [retryTick, setRetryTick] = useState(0);

  useEffect(() => {
    let errorCode = null; // "mfa_required" | "mfa_verify_required" | "role_denied" | "error"
    const F = (path, set) => api.get(path).then((r) => set(r.data)).catch((e) => {
      const detail = e?.response?.data?.detail;
      // GH-14 (real-plan certification, 2026-10-01) — `detail` stays a
      // plain string (dozens of other platform-admin pages already
      // assume that shape). The distinguishing code travels in the
      // `X-FOP-Error-Code` response header instead, which only this
      // component and PlatformAdmin read.
      const code = e?.response?.status === 403 ? e?.response?.headers?.["x-fop-error-code"] : null;
      if (code === "mfa_enrollment_required" || code === "mfa_verification_required") {
        errorCode = code === "mfa_enrollment_required" ? "mfa_required" : "mfa_verify_required";
      } else if (e?.response?.status === 403) {
        // No MFA code on a platform-admin-gated endpoint is a true
        // role denial — distinct terminal state, no retry button, no
        // generic toast spam.
        errorCode = "role_denied";
      } else {
        errorCode = "error";
        toast.error(`${path}: ${detail || "load failed"}`);
      }
      setLoadError(errorCode);
    });
    F("/platform/action_queue", setActions);
    F("/platform/health", setHealth);
    F("/platform/customer_success", setSuccess);
    F("/platform/support", setSupport);
    F("/platform/onboarding_progress", setOnb);
    F("/platform/adoption_analytics", setAdopt);
    F("/platform/reliability", setRel);
    F("/platform/cleanliness", setClean);
  }, [retryTick]);

  const ready = actions && health && success && support && onb && adopt && rel && clean;

  if (loadError === "mfa_required") {
    return (
      <div className="fop-surface-2 rounded-lg p-8 text-center max-w-lg mx-auto" data-testid="command-center-mfa-required">
        <ShieldAlert size={28} className="text-amber-400 mx-auto mb-3" />
        <h3 className="font-display text-xl text-[var(--fop-text)] mb-2">Two-factor authentication required</h3>
        <p className="text-sm text-[var(--fop-text-3)] mb-5">
          Platform admin actions now require MFA on your account. Set it up once — it takes under a minute — then come back here.
        </p>
        <Link to="/settings" className="btn btn-primary text-sm inline-flex" data-testid="command-center-mfa-enroll-link">
          Set up two-factor authentication <ArrowRight size={13} />
        </Link>
      </div>
    );
  }
  if (loadError === "mfa_verify_required") {
    return (
      <div className="fop-surface-2 rounded-lg p-8 text-center max-w-lg mx-auto" data-testid="command-center-mfa-verify-required">
        <ShieldAlert size={28} className="text-amber-400 mx-auto mb-3" />
        <h3 className="font-display text-xl text-[var(--fop-text)] mb-2">Verification needed</h3>
        <p className="text-sm text-[var(--fop-text-3)] mb-5">
          Your session's two-factor verification has expired or wasn't completed. Log out and log back in to re-verify.
        </p>
        <button
          onClick={() => { localStorage.clear(); window.location.href = "/login"; }}
          className="btn btn-primary text-sm"
          data-testid="command-center-mfa-reverify-logout"
        >
          Log out &amp; log back in
        </button>
      </div>
    );
  }
  if (loadError === "role_denied") {
    return (
      <div className="fop-surface-2 rounded-lg p-8 text-center max-w-lg mx-auto" data-testid="command-center-role-denied">
        <ShieldAlert size={28} className="text-red-400 mx-auto mb-3" />
        <h3 className="font-display text-xl text-[var(--fop-text)] mb-2">Platform administrator access required</h3>
        <p className="text-sm text-[var(--fop-text-3)] mb-5">
          Your account doesn't have platform administrator access. Contact a ForgedOps operator if you believe this is wrong.
        </p>
      </div>
    );
  }
  if (loadError === "error" && !ready) {
    return (
      <div className="fop-surface-2 rounded-lg p-8 text-center max-w-lg mx-auto" data-testid="command-center-load-error">
        <AlertTriangle size={28} className="text-red-400 mx-auto mb-3" />
        <h3 className="font-display text-xl text-[var(--fop-text)] mb-2">Command Center couldn't load</h3>
        <p className="text-sm text-[var(--fop-text-3)] mb-5">One or more platform endpoints failed. Check the notifications above for details.</p>
        <button onClick={() => { setLoadError(null); setRetryTick((t) => t + 1); }} className="btn btn-primary text-sm" data-testid="command-center-retry">
          Retry
        </button>
      </div>
    );
  }
  if (!ready) return <Loading label="Loading Command Center…" />;

  const reloadActions = () => api.get("/platform/action_queue").then((r) => setActions(r.data)).catch(() => {});

  /* Inline actions ─ Resend invite + Snooze (per priority row) */
  const resendInvite = async (act) => {
    try {
      const r = await api.post(`/platform/tenants/${act.tenant_id}/resend_owner_invite`);
      const url = r.data?.invitation?.url;
      const ownerEmail = r.data?.invitation?.owner_email;
      const fullUrl = url ? window.location.origin + url : "";
      let copied = false;
      if (fullUrl && navigator?.clipboard) {
        try { await navigator.clipboard.writeText(fullUrl); copied = true; } catch { /* clipboard blocked */ }
      }
      setRecentResend({ tenant_id: act.tenant_id, tenant_name: act.tenant_name, owner_email: ownerEmail, url: fullUrl, copied });
      toast.success(`Invite reissued for ${ownerEmail || act.tenant_name}${copied ? " — URL copied" : ""}`);
    } catch (e) { toast.error(errMsg(e, "Resend failed")); }
  };
  const snoozeAction = async (act) => {
    // Optimistic removal — update local state immediately, then sync with server
    setActions((prev) => prev ? {
      ...prev,
      count: Math.max(0, (prev.count || 0) - 1),
      snoozed_count: (prev.snoozed_count || 0) + 1,
      actions: (prev.actions || []).filter((a) => a.key !== act.key),
      by_priority: { ...(prev.by_priority || {}), [act.priority]: Math.max(0, (prev.by_priority?.[act.priority] || 0) - 1) },
    } : prev);
    try {
      await api.post("/platform/action_snooze", { tenant_id: act.tenant_id, category: act.category, days: 7 });
      toast.success(`Snoozed ${act.category} for ${act.tenant_name} · 7 days`);
    } catch (e) {
      toast.error(errMsg(e, "Snooze failed"));
      await reloadActions(); // revert on error
    }
  };

  /* ── 5-second answer ── */
  const overall = health.overall || "amber";
  const a = actions || {};
  const bp = a.by_priority || {};
  const p0 = bp.p0 || 0;
  const p1 = bp.p1 || 0;
  const top = (a.actions || []).slice(0, 5);
  const totalActions = a.count || 0;

  /* ── Triage tile metrics ── */
  const brokenSurfaces = (() => {
    const s = rel.surfaces_24h || {};
    return Object.entries(s).filter(([, v]) => v.total > 0 && v.success_rate_pct < 95).map(([k]) => k);
  })();
  const reliabilityAccent = brokenSurfaces.length ? "red" : (rel.infra?.email?.configured && rel.infra?.webhook?.configured ? "green" : "amber");

  const stuckOnb = (onb || []).filter((r) => r.stage === "stuck" || r.stage === "started").length;
  const atRiskTenants = (success.at_risk || []).length;
  const supportCount = (support.summary?.open_discoveries || 0) + (support.summary?.open_feedback || 0);
  const silentCount = adopt.verdict?.silent || 0;
  const cleanCount = (clean.summary?.test_tenants || 0) + (clean.summary?.demo_tenants || 0) + (clean.summary?.orphan_users || 0);

  const overallChipState = overall === "green" ? "green" : overall === "red" ? "red" : "amber";
  const overallLine =
    overall === "green" ? "Platform is healthy. No action required."
    : overall === "red" ? "Critical issues detected — open the items below."
    : "Attention required — open the items below.";

  return (
    <div className="space-y-4" data-testid="command-center">

      {/* ─── 5-Second Hero ─── */}
      <div className="fop-surface-2 rounded-lg p-5 border border-[var(--fop-border)] flex items-center gap-4 flex-wrap" data-testid="command-hero">
        <div className={`w-14 h-14 rounded-full flex items-center justify-center border ${overall === "green" ? "border-emerald-500/40 bg-emerald-500/10" : overall === "red" ? "border-red-500/40 bg-red-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
          {overall === "green" ? <CheckCircle2 size={24} className="text-emerald-400" /> : <AlertTriangle size={24} className={overall === "red" ? "text-red-400" : "text-amber-400"} />}
        </div>
        <div className="flex-1 min-w-[280px]">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)]">Status</div>
          <div className="flex items-baseline gap-2 mt-0.5">
            <h2 className="font-display text-2xl text-[var(--fop-text)]">{overall.toUpperCase()}</h2>
            <span className="text-[var(--fop-text-3)] text-sm">·</span>
            <span className="text-[var(--fop-text-2)] text-sm">{overallLine}</span>
          </div>
        </div>
        <div className="text-right">
          <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--fop-text-3)]">Needs attention</div>
          <div className="font-display text-4xl text-[var(--fop-primary)]" data-testid="hero-action-count">{totalActions}</div>
          <div className="font-mono text-[10px] text-[var(--fop-text-4)]">{p0 ? `${p0} P0 · ` : ""}{p1 ? `${p1} P1` : (p0 ? "" : "0 critical")}</div>
        </div>
      </div>

      {/* ─── Persistent resend confirmation (sticky until dismissed) ─── */}
      {recentResend && (
        <div className="fop-surface-2 rounded-lg p-3 border border-emerald-500/40 bg-emerald-500/5 flex items-center gap-3 flex-wrap" data-testid="resend-confirmation">
          <CheckCircle2 size={16} className="text-emerald-400" />
          <div className="flex-1 min-w-[240px]">
            <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-300">Invite reissued · {recentResend.tenant_name}</div>
            <div className="text-[11px] text-[var(--fop-text-3)] font-mono break-all">{recentResend.owner_email} · {recentResend.url}</div>
          </div>
          {recentResend.copied && <span className="text-[10px] font-mono text-emerald-300">CLIPBOARD ✓</span>}
          {!recentResend.copied && recentResend.url && (
            <button onClick={() => navigator?.clipboard?.writeText(recentResend.url)?.then?.(() => toast.success("Copied"))}
              data-testid="resend-copy-fallback"
              className="text-[10px] font-mono uppercase tracking-wider text-[var(--fop-text-2)] hover:text-[var(--fop-primary)]">Copy URL</button>
          )}
          <button onClick={() => setRecentResend(null)} data-testid="resend-dismiss" className="text-[var(--fop-text-3)] hover:text-[var(--fop-text)]"><X size={14} /></button>
        </div>
      )}

      {/* ─── The Answer: Top 5 actions ─── */}
      <div className="fop-surface-2 rounded-lg p-5" data-testid="command-priority-list">
        <div className="flex items-center justify-between mb-3">
          <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] inline-flex items-center gap-1.5">
            <Target size={11} /> What needs your attention right now
          </div>
          {totalActions > 5 && (
            <button onClick={() => setDrawer("actions")} className="text-[10px] font-mono text-[var(--fop-primary)] hover:underline inline-flex items-center gap-1" data-testid="view-all-actions">
              View all {totalActions} <ArrowRight size={11} />
            </button>
          )}
        </div>
        {top.length === 0 ? (
          <div className="py-6 text-center text-[var(--fop-text-3)] text-sm inline-flex items-center justify-center gap-2 w-full">
            <CheckCircle2 size={16} className="text-emerald-400" /> Nothing in the queue. The platform is operating normally.
          </div>
        ) : (
          <div className="space-y-1.5">
            {top.map((act, i) => (
              <div key={act.key || `${act.tenant_id}-${i}`} data-testid={`priority-action-${i}`} data-testkey={act.key}
                className="flex items-center gap-3 px-3 py-2 rounded border border-[var(--fop-border)] hover:border-[var(--fop-primary)]/40 hover:bg-[var(--fop-surface)] transition group">
                <Chip state={act.priority} label={act.priority.toUpperCase()} />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-[var(--fop-text)] truncate">{act.title}</div>
                  <div className="text-[11px] text-[var(--fop-text-3)] truncate">{act.tenant_name} · {act.suggestion}</div>
                </div>
                {act.category === "onboarding" && act.owner_email && (
                  <button onClick={(e) => { e.stopPropagation(); resendInvite(act); }} data-testid={`action-resend-${i}`}
                    className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--fop-border-2)] text-[var(--fop-text-2)] hover:bg-[var(--fop-primary)] hover:text-black inline-flex items-center gap-1.5"
                    title="Resend invite — copies URL to clipboard">
                    <Send size={11} /> Resend
                  </button>
                )}
                <button onClick={(e) => { e.stopPropagation(); snoozeAction(act); }} data-testid={`action-snooze-${i}`}
                  className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded border border-[var(--fop-border-2)] text-[var(--fop-text-3)] hover:bg-[var(--fop-surface-2)] inline-flex items-center gap-1.5"
                  title="Snooze for 7 days">
                  <Clock size={11} /> Snooze
                </button>
                <Link to={`/platform/tenants/${act.tenant_id}`} data-testid={`action-open-${i}`}
                  className="px-2 py-1 text-[10px] font-mono uppercase tracking-wider rounded text-[var(--fop-text-3)] hover:text-[var(--fop-primary)] inline-flex items-center gap-1.5"
                  title="Open tenant">
                  Open <ArrowRight size={11} />
                </Link>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ─── Triage Tiles (click to drill-down) ─── */}
      <div>
        <div className="font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--fop-text-3)] mb-2">Triage · click to drill in</div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Tile testid="tile-reliability"
            accent={reliabilityAccent}
            label="Broken"
            value={brokenSurfaces.length}
            sub={brokenSurfaces.length ? brokenSurfaces.slice(0, 2).join(", ") : "All surfaces healthy"}
            onClick={() => setDrawer("reliability")} />
          <Tile testid="tile-onboarding"
            accent={stuckOnb > 0 ? "amber" : "green"}
            label="Stuck onboarding"
            value={stuckOnb}
            sub={`${(onb || []).length} active tenants`}
            onClick={() => setDrawer("onboarding")} />
          <Tile testid="tile-at-risk"
            accent={atRiskTenants > 0 ? "red" : "green"}
            label="At-risk customers"
            value={atRiskTenants}
            sub="Health score < 50"
            onClick={() => setDrawer("at_risk")} />
          <Tile testid="tile-support"
            accent={supportCount > 0 ? "amber" : "green"}
            label="Open support"
            value={supportCount}
            sub={`${support.summary?.incidents_72h || 0} incidents · 72h`}
            onClick={() => setDrawer("support")} />
          <Tile testid="tile-silent"
            accent={silentCount > 0 ? "amber" : "green"}
            label="Silent customers"
            value={silentCount}
            sub="Zero activity · 30d"
            onClick={() => setDrawer("adoption")} />
          <Tile testid="tile-cleanup"
            accent={cleanCount > 0 ? "zinc" : "green"}
            label="Cleanup queue"
            value={cleanCount}
            sub="Test · demo · orphans"
            onClick={() => setDrawer("cleanliness")} />
        </div>
      </div>

      <div className="text-center pt-2">
        <button onClick={onOpenOnboarding} data-testid="command-cta-onboard"
          className="btn btn-ghost text-[11px] inline-flex items-center gap-1.5 text-[var(--fop-text-3)] hover:text-[var(--fop-primary)]">
          <Rocket size={12} /> Onboard a new customer
        </button>
      </div>

      {/* ─── Drill-down drawers ─── */}
      <Drawer open={drawer === "actions"} onClose={() => setDrawer(null)} title="Action Queue · all">
        <div className="space-y-1.5">
          {(a.actions || []).map((act, i) => (
            <Link key={`${act.tenant_id}-${i}`} to={`/platform/tenants/${act.tenant_id}`} onClick={() => setDrawer(null)}
              className="flex items-center gap-3 px-3 py-2 rounded border border-[var(--fop-border)] hover:border-[var(--fop-primary)]/40">
              <Chip state={act.priority} label={act.priority.toUpperCase()} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] text-[var(--fop-text)] truncate">{act.title}</div>
                <div className="text-[11px] text-[var(--fop-text-3)] truncate">{act.tenant_name} · {act.suggestion}</div>
              </div>
              <ArrowRight size={14} className="text-[var(--fop-text-4)]" />
            </Link>
          ))}
        </div>
      </Drawer>
      <Drawer open={drawer === "reliability"} onClose={() => setDrawer(null)} title="Reliability · success rates"><ReliabilityCenter /></Drawer>
      <Drawer open={drawer === "onboarding"} onClose={() => setDrawer(null)} title="Onboarding · per-tenant progress"><OnboardingCommand /></Drawer>
      <Drawer open={drawer === "support"} onClose={() => setDrawer(null)} title="Support · discoveries · feedback · incidents"><SupportCenter /></Drawer>
      <Drawer open={drawer === "adoption"} onClose={() => setDrawer(null)} title="Adoption · feature usage 30d"><AdoptionAnalytics /></Drawer>
      <Drawer open={drawer === "cleanliness"} onClose={() => setDrawer(null)} title="Production Cleanliness"><CleanlinessCenter /></Drawer>
      <Drawer open={drawer === "at_risk"} onClose={() => setDrawer(null)} title="At-risk customers · health < 50">
        <table className="w-full text-xs">
          <thead className="bg-[var(--fop-surface)] text-[var(--fop-text-3)]"><tr><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Tenant</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Owner</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Health</th><th className="px-3 py-2 text-left text-[10px] uppercase tracking-wider font-mono">Last login</th></tr></thead>
          <tbody>
            {(success.at_risk || []).map((t) => (
              <tr key={t.tenant_id} className="border-t border-[var(--fop-border)]">
                <td className="px-3 py-2"><Link to={`/platform/tenants/${t.tenant_id}`} onClick={() => setDrawer(null)} className="hover:text-[var(--fop-primary)]">{t.tenant_name}</Link></td>
                <td className="px-3 py-2 font-mono text-[10px] text-[var(--fop-text-3)]">{t.owner_email || "—"}</td>
                <td className="px-3 py-2"><Chip state="red" label={`${t.health_score}/100`} /></td>
                <td className="px-3 py-2 font-mono text-[10px] text-[var(--fop-text-4)]">{t.days_since_login != null ? `${t.days_since_login}d` : "never"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Drawer>
    </div>
  );
}
